import type { SelectComponentFilters } from 'types/selectComponent'
import { SlotType } from 'types/slots'
import { StudioSystemType } from 'types/global'
import { updateSlot } from 'projectSections/sections/design/systems/Slots'
import { V2Component } from './autoSelectTypes'
import { SLOT_AUTO_SELECT_NOTE_KEY } from './constants'
import {
  componentIsFromPreferredSupplier,
  componentIsFromSupplier,
  getPreferredSupplierKeyForComponent,
  getSupplierAttemptOrder,
  pickFirstAtPreferredSupplier,
  selectFromCatalogWithSupplierFallback,
} from './supplierPreferenceUtils'
import {
  normalizeACIsolator,
  passesACIsolatorCurrentPickFilter,
} from './autoSelectCompatibility'
import { getACIsolatorOutputCurrent } from './helpers'
import {
  buildPhaseEscalationNote,
  buildSizeEscalationNote,
  IsolatorSelectionNote,
} from './isolatorSelectionNote'
import type { IsolatorSelectionTrace } from './autoElectricalSelectionTrace'
import { componentSupplierKey, summarizeACIsolatorPreferredStock, summarizeDCIsolatorStockedRatings } from './autoElectricalSelectionTrace'

export const ISOLATOR_CURRENT_HEADROOM_FACTOR = 1.25

export type IsolatorPickResult = {
  match: V2Component | null
  selectionNote?: IsolatorSelectionNote
  trace?: IsolatorSelectionTrace
}

export function isIsolatorSlot(slot: SlotType): boolean {
  return slot.type === 'ac_isolator' || slot.type === 'dc_isolator'
}

export function isACIsolatorSlot(slot: SlotType): boolean {
  return slot.type === 'ac_isolator'
}

export function getMinimumACIsolatorCurrentRating(outputCurrentAmps: number): number {
  return outputCurrentAmps * ISOLATOR_CURRENT_HEADROOM_FACTOR
}

export function getMinimumACIsolatorCurrentRatingForSlot(slot: SlotType): number | null {
  if (!isACIsolatorSlot(slot) || !slot.attachToUuid) return null
  const isolatorLocation = window.editor?.objectByUuid?.(slot.attachToUuid)
  const outputCurrent = getACIsolatorOutputCurrent(isolatorLocation)
  return outputCurrent != null ? getMinimumACIsolatorCurrentRating(outputCurrent) : null
}

/**
 * Preferred-supplier fallbacks for single-phase inverters: try matching single-phase
 * SKUs first, then three-phase (e.g. 3-pole isolator on single-phase supply).
 */
export function getACIsolatorPhaseAttempts(inverterPhase?: string): string[] {
  if (inverterPhase === 'single_phase') return ['single_phase', 'three_phase']
  if (inverterPhase === 'three_phase') return ['three_phase']
  return []
}

export function filterSettingsForIsolatorCatalogFetch(
  filterSettings: SelectComponentFilters,
  slot: SlotType
): SelectComponentFilters {
  if (slot.type === 'ac_isolator') {
    const minimumCurrent = getMinimumACIsolatorCurrentRatingForSlot(slot)
    const { phase_type: _phaseType, current_rating_range: _currentRange, ...rest } = filterSettings
    if (minimumCurrent == null) return rest
    return {
      ...rest,
      current_rating_range: `${minimumCurrent},1000`,
    }
  }

  if (slot.type === 'dc_isolator') {
    const { voltage_to_current_rating: _vtc, ...rest } = filterSettings
    return rest
  }

  return filterSettings
}

function buildACIsolatorSelectionNote(
  slot: SlotType,
  match: V2Component,
  components: V2Component[],
  orderedSuppliers: string[],
  minimumCurrent: number,
  inverterPhase?: string
): IsolatorSelectionNote | undefined {
  if (inverterPhase !== 'single_phase' || match.phase_type !== 'three_phase') {
    const preferredSinglePhase = components
      .filter(
        (component) =>
          component.phase_type === 'single_phase' && Number(component.current_rating) >= minimumCurrent
      )
      .sort((a, b) => Number(a.current_rating) - Number(b.current_rating))

    const smallestPreferredSinglePhase = pickFirstAtPreferredSupplier(
      preferredSinglePhase,
      orderedSuppliers,
      false
    )
    const selectedRating = Number(match.current_rating)
    if (
      match.phase_type === 'single_phase' &&
      smallestPreferredSinglePhase &&
      Number(smallestPreferredSinglePhase.current_rating) < selectedRating &&
      match.code === pickFirstAtPreferredSupplier(preferredSinglePhase, orderedSuppliers, false)?.code
    ) {
      return buildSizeEscalationNote(
        selectedRating,
        getPreferredSupplierKeyForComponent(match, orderedSuppliers)
      )
    }
    return undefined
  }

  const preferredHasSinglePhase = components.some(
    (component) =>
      component.phase_type === 'single_phase' &&
      Number(component.current_rating) >= minimumCurrent &&
      componentIsFromPreferredSupplier(component, orderedSuppliers)
  )

  if (!preferredHasSinglePhase) {
    return buildPhaseEscalationNote(getPreferredSupplierKeyForComponent(match, orderedSuppliers))
  }

  return undefined
}

function pickSmallestAdequateACIsolatorAtSupplier(
  components: V2Component[],
  supplier: string,
  phase: string,
  outputCurrent: number
): V2Component | null {
  const matches = components
    .filter(
      (component) =>
        component.phase_type === phase &&
        passesACIsolatorCurrentPickFilter(component, outputCurrent) &&
        componentIsFromSupplier(component, supplier)
    )
    .sort((a, b) => Number(a.current_rating) - Number(b.current_rating))

  return matches[0] ?? null
}

type ACIsolatorPickContext = {
  outputCurrent: number
  minimumCurrent: number
  inverterPhase?: string
  phaseAttempts: string[]
  preferredStockBySupplier: IsolatorSelectionTrace['preferredStockBySupplier']
  orderedSuppliers: string[]
}

function createACIsolatorPickContext(
  components: V2Component[],
  orderedSuppliers: string[],
  minimumCurrent: number,
  outputCurrent: number,
  inverterPhase?: string
): ACIsolatorPickContext {
  return {
    outputCurrent,
    minimumCurrent,
    inverterPhase,
    phaseAttempts: getACIsolatorPhaseAttempts(inverterPhase),
    preferredStockBySupplier: summarizeACIsolatorPreferredStock(components, orderedSuppliers, minimumCurrent),
    orderedSuppliers,
  }
}

function buildACIsolatorTrace(
  ctx: ACIsolatorPickContext,
  requirePreferredSupplier: boolean,
  options: {
    match?: V2Component | null
    selectedSupplier?: string
    supplierEscalated?: boolean
    selectionNote?: IsolatorSelectionNote
  } = {}
): IsolatorSelectionTrace {
  const { match, selectionNote, selectedSupplier, supplierEscalated = false } = options
  const selectedRating = match ? Number(match.current_rating) : NaN

  return {
    catalogScopeEscalated: !requirePreferredSupplier,
    supplierEscalated,
    outputCurrent: ctx.outputCurrent,
    minimumRating: ctx.minimumCurrent,
    inverterPhase: ctx.inverterPhase,
    phaseAttempts: ctx.phaseAttempts,
    preferredStockBySupplier: ctx.preferredStockBySupplier,
    orderedSuppliers: [...ctx.orderedSuppliers],
    selectedPhase: match?.phase_type,
    selectedRating: Number.isFinite(selectedRating) ? selectedRating : undefined,
    selectedSupplier,
    phaseEscalated: ctx.inverterPhase === 'single_phase' && match?.phase_type === 'three_phase',
    sizeEscalated: selectionNote?.type === 'size_escalation',
  }
}

export function pickACIsolatorFromComponents(
  components: V2Component[],
  slot: SlotType,
  orderedSuppliers: string[],
  requirePreferredSupplier: boolean
): IsolatorPickResult {
  if (!slot.attachToUuid) return { match: null }
  const isolatorLocation = window.editor?.objectByUuid?.(slot.attachToUuid)
  const inverter = isolatorLocation?.type === 'OsMppt' ? isolatorLocation.parent : isolatorLocation
  const outputCurrent = getACIsolatorOutputCurrent(isolatorLocation)
  const minimumCurrent = outputCurrent != null ? getMinimumACIsolatorCurrentRating(outputCurrent) : null
  if (minimumCurrent == null || outputCurrent == null) return { match: null }

  const normalizedComponents = components.map(normalizeACIsolator)
  const ctx = createACIsolatorPickContext(
    normalizedComponents,
    orderedSuppliers,
    minimumCurrent,
    outputCurrent,
    inverter?.phase_type
  )
  const suppliersToTry = getSupplierAttemptOrder(normalizedComponents, orderedSuppliers, requirePreferredSupplier)
  const firstPreferredSupplier = orderedSuppliers[0]

  for (const supplier of suppliersToTry) {
    for (const phase of ctx.phaseAttempts) {
      const match = pickSmallestAdequateACIsolatorAtSupplier(normalizedComponents, supplier, phase, outputCurrent)
      if (match) {
        const selectionNote = buildACIsolatorSelectionNote(
          slot,
          match,
          normalizedComponents,
          orderedSuppliers,
          minimumCurrent,
          inverter?.phase_type
        )
        return {
          match,
          selectionNote,
          trace: buildACIsolatorTrace(ctx, requirePreferredSupplier, {
            match,
            selectedSupplier: supplier,
            supplierEscalated: !!firstPreferredSupplier && supplier !== firstPreferredSupplier,
            selectionNote,
          }),
        }
      }
    }
  }

  if (!orderedSuppliers.length) {
    for (const phase of ctx.phaseAttempts) {
      const phaseMatches = normalizedComponents
        .filter(
          (component) =>
            component.phase_type === phase && passesACIsolatorCurrentPickFilter(component, outputCurrent)
        )
        .sort((a, b) => Number(a.current_rating) - Number(b.current_rating))

      if (phaseMatches.length > 0) {
        const match = phaseMatches[0]
        const selectionNote = buildACIsolatorSelectionNote(
          slot,
          match,
          normalizedComponents,
          orderedSuppliers,
          minimumCurrent,
          inverter?.phase_type
        )
        return {
          match,
          selectionNote,
          trace: buildACIsolatorTrace(ctx, requirePreferredSupplier, {
            match,
            selectedSupplier: componentSupplierKey(match, orderedSuppliers),
            selectionNote,
          }),
        }
      }
    }
  }

  return {
    match: null,
    trace: buildACIsolatorTrace(ctx, requirePreferredSupplier),
  }
}

function buildDCIsolatorTrace(
  preferredStockedRatings: number[],
  options: {
    catalogScopeEscalated: boolean
    supplierEscalated?: boolean
    match?: V2Component | null
    selectedSupplier?: string
  }
): IsolatorSelectionTrace {
  const selectedRating = options.match ? Number(options.match.current_rating) : NaN
  return {
    catalogScopeEscalated: options.catalogScopeEscalated,
    supplierEscalated: options.supplierEscalated ?? false,
    preferredStockedRatings,
    selectedRating: Number.isFinite(selectedRating) ? selectedRating : undefined,
    selectedPhase: options.match?.phase_type,
    selectedSupplier: options.selectedSupplier,
    phaseEscalated: false,
    sizeEscalated: false,
  }
}

function pickIsolatorCatalogOnlyFallback(
  components: V2Component[],
  slot: SlotType,
  orderedSuppliers: string[]
): IsolatorPickResult | null {
  if (!components.length) return null

  if (slot.type === 'ac_isolator') {
    if (!slot.attachToUuid) return null
    const isolatorLocation = window.editor?.objectByUuid?.(slot.attachToUuid)
    const inverter = isolatorLocation?.type === 'OsMppt' ? isolatorLocation.parent : isolatorLocation
    const outputCurrent = getACIsolatorOutputCurrent(isolatorLocation)
    const minimumCurrent = outputCurrent != null ? getMinimumACIsolatorCurrentRating(outputCurrent) : null
    if (minimumCurrent == null || outputCurrent == null) return null

    const normalizedComponents = components.map(normalizeACIsolator)
    const phaseAttempts = getACIsolatorPhaseAttempts(inverter?.phase_type)
    for (const phase of phaseAttempts) {
      const phaseMatches = normalizedComponents
        .filter(
          (component) =>
            component.phase_type === phase && passesACIsolatorCurrentPickFilter(component, outputCurrent)
        )
        .sort((a, b) => Number(a.current_rating) - Number(b.current_rating))
      if (!phaseMatches.length) continue

      const match = phaseMatches[0]
      const ctx = createACIsolatorPickContext(
        normalizedComponents,
        orderedSuppliers,
        minimumCurrent,
        outputCurrent,
        inverter?.phase_type
      )
      return {
        match,
        trace: {
          ...buildACIsolatorTrace(ctx, false, {
            match,
            selectedSupplier: componentSupplierKey(match, orderedSuppliers),
          }),
          catalogUnstockedFallback: true,
        },
      }
    }
    return null
  }

  if (slot.type === 'dc_isolator') {
    const sorted = [...components].sort((a, b) => {
      const aCurrent = Number(a?.current_rating)
      const bCurrent = Number(b?.current_rating)
      if (Number.isFinite(aCurrent) && Number.isFinite(bCurrent)) return aCurrent - bCurrent
      return String(a?.code || '').localeCompare(String(b?.code || ''))
    })
    const match = sorted[0]
    if (!match) return null

    const stocked = sorted.filter((component) => component.ordering_v2?.length)
    return {
      match,
      trace: {
        ...buildDCIsolatorTrace(summarizeDCIsolatorStockedRatings(stocked, orderedSuppliers), {
          catalogScopeEscalated: true,
          match,
          selectedSupplier: componentSupplierKey(match, orderedSuppliers),
        }),
        catalogUnstockedFallback: true,
      },
    }
  }

  return null
}

export function pickDCIsolatorFromComponents(
  components: V2Component[],
  orderedSuppliers: string[],
  requirePreferredSupplier: boolean
): IsolatorPickResult {
  const sorted = [...components].sort((a, b) => {
    const aCurrent = Number(a?.current_rating)
    const bCurrent = Number(b?.current_rating)
    if (Number.isFinite(aCurrent) && Number.isFinite(bCurrent)) return aCurrent - bCurrent
    return String(a?.code || '').localeCompare(String(b?.code || ''))
  })

  const stocked = sorted.filter((component) => component.ordering_v2?.length)
  const preferredStockedRatings = summarizeDCIsolatorStockedRatings(stocked, orderedSuppliers)
  const suppliersToTry = getSupplierAttemptOrder(stocked, orderedSuppliers, requirePreferredSupplier)
  const firstPreferredSupplier = orderedSuppliers[0]

  for (const supplier of suppliersToTry) {
    const match = stocked.find((component) => componentIsFromSupplier(component, supplier))
    if (match) {
      return {
        match,
        trace: buildDCIsolatorTrace(preferredStockedRatings, {
          catalogScopeEscalated: !requirePreferredSupplier,
          supplierEscalated: !!firstPreferredSupplier && supplier !== firstPreferredSupplier,
          match,
          selectedSupplier: supplier,
        }),
      }
    }
  }

  if (!requirePreferredSupplier && !orderedSuppliers.length && sorted.length > 0) {
    const match = sorted[0]
    return {
      match,
      trace: buildDCIsolatorTrace(preferredStockedRatings, {
        catalogScopeEscalated: false,
        match,
        selectedSupplier: componentSupplierKey(match, orderedSuppliers),
      }),
    }
  }

  return {
    match: null,
    trace: buildDCIsolatorTrace(preferredStockedRatings, {
      catalogScopeEscalated: !requirePreferredSupplier,
    }),
  }
}

export function pickIsolatorFromComponents(
  components: V2Component[],
  slot: SlotType,
  orderedSuppliers: string[],
  requirePreferredSupplier: boolean
): IsolatorPickResult {
  if (slot.type === 'ac_isolator') {
    return pickACIsolatorFromComponents(components, slot, orderedSuppliers, requirePreferredSupplier)
  }
  if (slot.type === 'dc_isolator') {
    return pickDCIsolatorFromComponents(components, orderedSuppliers, requirePreferredSupplier)
  }
  return { match: null }
}

export async function selectIsolatorMatch(params: {
  slot: SlotType
  filterSettings: SelectComponentFilters
  orderedSuppliers: string[]
  fetchComponents: (
    settings: SelectComponentFilters,
    scopedToPreferredSuppliers: boolean
  ) => Promise<V2Component[]>
  refineComponents?: (components: V2Component[]) => V2Component[]
}): Promise<{
  match: V2Component | null
  totalFound: number
  selectionNote?: IsolatorSelectionNote
  selectionTrace?: IsolatorSelectionTrace
}> {
  const catalogSettings = filterSettingsForIsolatorCatalogFetch(params.filterSettings, params.slot)
  let lastTrace: IsolatorSelectionTrace | undefined

  const { match, totalFound, catalogFetchScope } = await selectFromCatalogWithSupplierFallback<
    V2Component,
    IsolatorPickResult
  >({
    orderedSuppliers: params.orderedSuppliers,
    fetchComponents: (scopedToPreferredSuppliers) =>
      params.fetchComponents(catalogSettings, scopedToPreferredSuppliers),
    refineComponents: params.refineComponents,
    pick: (components, requirePreferredSupplier) => {
      const result = pickIsolatorFromComponents(
        components,
        params.slot,
        params.orderedSuppliers,
        requirePreferredSupplier
      )
      if (result.trace) {
        lastTrace = result.trace
      }
      return result.match ? result : null
    },
    fallbackPick: (components) =>
      pickIsolatorCatalogOnlyFallback(components, params.slot, params.orderedSuppliers),
  })

  const selectionTrace = (match?.trace ?? lastTrace)
    ? {
        ...(match?.trace ?? lastTrace)!,
        catalogFetchScope,
        catalogScopeEscalated:
          (match?.trace ?? lastTrace)!.catalogScopeEscalated || catalogFetchScope === 'full_catalog',
      }
    : undefined

  return {
    match: match?.match ?? null,
    totalFound,
    selectionNote: match?.selectionNote,
    selectionTrace,
  }
}

export function setSlotAutoSelectNote(
  slot: SlotType,
  system: StudioSystemType,
  selectionNote?: IsolatorSelectionNote
): void {
  const properties = { ...(slot.properties || {}) } as Record<string, unknown>
  if (selectionNote) {
    properties[SLOT_AUTO_SELECT_NOTE_KEY] = selectionNote
  } else {
    delete properties[SLOT_AUTO_SELECT_NOTE_KEY]
  }
  updateSlot({ ...slot, properties }, system)
}
